import React, { useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { toast } from 'react-toastify';
import './AddEditFee.css';
import { handlePostRequest } from '../../services/PostTemplate';
import { useDispatch } from 'react-redux';
import { handlePutRequest } from '../../services/PutTemplate';
import Loader from '../../components/loader';

function AddEditFee({ onHideSidebar, isEditing, editData, fetchFeesData }) {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const feeSchema = Yup.object().shape({
        categoryName: Yup.string()
            .required('Vehicle Category is required')
            .min(2, 'Vehicle Category must be at least 2 characters'),
        registrationFee: Yup.number()
            .required('Registration Fee is required')
            .min(1, 'Registration Fee must be greater than zero')
            .typeError('Registration Fee must be a number'),
        finePerMonth: Yup.number()
            .required('Fine Per Month is required')
            .min(1, 'Fine Per Month must be greater than zero')
            .typeError('Fine Per Month must be a number'),
        renewalFee: Yup.number()
            .required('Renewal Fee is required')
            .min(1, 'Renewal Fee must be greater than zero')
            .typeError('Renewal Fee must be a number'),
        expiryDuration: Yup.number()
            .required('Certificate Expiry Period is required')
            .min(1, 'Certificate Expiry Period must be at least 1 year')
            .max(5, 'Certificate Expiry Period cannot exceed 5 years')
            .typeError('Certificate Expiry Period must be a number')
    });

    const formik = useFormik({
        initialValues: {
            categoryName: editData?.categoryName || '',
            registrationFee: editData?.registrationFee || null,
            // Map finePerDay to finePerMonth if editing an existing record
            finePerMonth: editData?.finePerMonth || editData?.finePerDay || null,
            renewalFee: editData?.renewalFee || null,
            expiryDuration: editData?.expiryDuration || null
        },
        validationSchema: feeSchema,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const feeData = {
                    categoryName: values.categoryName,
                    registrationFee: values.registrationFee || 0,
                    gracePeriod: 0, // Set grace period to 0 as per requirement
                    finePerMonth: values.finePerMonth || 0,
                    renewalFee: values.renewalFee || 0,
                    expiryDuration: Number(values.expiryDuration)
                };

                if (isEditing) {
                    feeData.id = editData.id;
                    const response = await dispatch(handlePutRequest(feeData, '/api/permit/fee', false, false));
                    console.log('Response:', response);
                    if (response?.success) {
                        fetchFeesData();
                        onHideSidebar();
                        toast.success('Registration fee updated successfully');
                    }
                    else {
                        toast.error(response?.message || 'Failed to update registration fee');
                    }
                } else {
                    const response = await dispatch(handlePostRequest(feeData, '/api/permit/fee',false,false));
                    if (response?.success) {
                        fetchFeesData();
                        onHideSidebar();
                        toast.success('Registration fee added successfully');
                    }
                    else {
                        toast.error(response?.message || 'Failed to add registration fee');
                    }
                }
            } catch (error) {
                console.error('Error:', error);
                toast.error('Failed to process registration fee');
            } finally {
                setLoading(false);
            }
        }
    });

    return (
        <>
            {loading && (
                <div
                    className="fixed top-0 left-0 w-full h-full flex items-center justify-center"
                    style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(2px)',
                        zIndex: 1000
                    }}
                >
                    <Loader />
                </div>
            )}
            <div className="header-content">
                <h2>{isEditing ? 'Edit Registration Fee' : 'Add Registration Fee'}</h2>
                <Button icon="pi pi-times" onClick={onHideSidebar} className="close-button" />
            </div>

            {/* Form Content */}
            <div className="p-4">
                <form onSubmit={formik.handleSubmit} className="mt-2">
                    <div className="">
                        {/* Vehicle Category Field */}
                        <div className="form-group">
                            <label className="form-label">Vehicle Category</label>
                            <InputText
                                id="categoryName"
                                name="categoryName"
                                value={formik.values.categoryName}
                                onChange={formik.handleChange}
                                // onBlur={formik.handleBlur}
                                className={`Input__Round ${ formik.errors.categoryName ? 'is-invalid' : ''}`}
                                placeholder="Enter Vehicle Category"
                            />
                            { formik.errors.categoryName && <div className="invalid-feedback">{formik.errors.categoryName}</div>}
                        </div>

                        {/* Renewal Fee Field */}
                        <div className="form-group">
                            <label className="form-label">Renewal Fee</label>
                            <InputNumber
                                id="renewalFee"
                                name="renewalFee"
                                value={formik.values.renewalFee}
                                onValueChange={(e) => {
                                    // Always set the value provided by the component
                                    formik.setFieldValue('renewalFee', e.value);
                                }}
                                // onBlur={formik.handleBlur}
                                mode="currency"
                                currency="PKR"
                                min={0}
                                minFractionDigits={0}
                                maxFractionDigits={0}
                                useGrouping={false}
                                className={`Input__Round ${ formik.errors.renewalFee ? 'is-invalid' : ''}`}
                                placeholder="Enter renewal fee"
                            />
                            { formik.errors.renewalFee && <div className="invalid-feedback">{formik.errors.renewalFee}</div>}
                        </div>

                        {/* Registration Fee Field */}
                        <div className="form-group">
                            <label className="form-label">Registration Fee</label>
                            <InputNumber
                                id="registrationFee"
                                name="registrationFee"
                                value={formik.values.registrationFee}
                                onValueChange={(e) => {
                                    // Always set the value provided by the component
                                    formik.setFieldValue('registrationFee', e.value);
                                }}
                                // onBlur={formik.handleBlur}
                                mode="currency"
                                currency="PKR"
                                min={0}
                                minFractionDigits={0}
                                maxFractionDigits={0}
                                className={`Input__Round ${ formik.errors.registrationFee ? 'is-invalid' : ''}`}
                                placeholder="Enter registration fee"
                            />
                            { formik.errors.registrationFee && <div className="invalid-feedback">{formik.errors.registrationFee}</div>}
                        </div>

                        {/* Fine Per Month Field - Updated from finePerDay */}
                        <div className="form-group">
                            <label className="form-label">Fine Per Month</label>
                            <InputNumber
                                id="finePerMonth"
                                name="finePerMonth"
                                value={formik.values.finePerMonth}
                                onValueChange={(e) => {
                                    // Always set the value provided by the component
                                    formik.setFieldValue('finePerMonth', e.value);
                                }}
                                // onBlur={formik.handleBlur}
                                mode="currency"
                                currency="PKR"
                                min={0}
                                minFractionDigits={0}
                                maxFractionDigits={0}
                                className={`Input__Round ${ formik.errors.finePerMonth ? 'is-invalid' : ''}`}
                                placeholder="Enter fine per month"
                            />
                            { formik.errors.finePerMonth && <div className="invalid-feedback">{formik.errors.finePerMonth}</div>}
                        </div>

                        {/* Certificate Expiry Period Field */}
                        <div className="form-group">
                            <label className="form-label">Certificate Expiry Period</label>
                            <Dropdown
                                id="expiryDuration"
                                name="expiryDuration"
                                value={formik.values.expiryDuration}
                                options={[
                                    { label: '1 Year', value: 1 },
                                    { label: '2 Years', value: 2 },
                                    { label: '3 Years', value: 3 },
                                    { label: '4 Years', value: 4 },
                                    { label: '5 Years', value: 5 }
                                ]}
                                onChange={formik.handleChange}
                                // onBlur={formik.handleBlur}
                                className={`${ formik.errors.expiryDuration ? 'is-invalid' : 'Dropdown__Round w-full'}`}
                                placeholder="Select certificate expiry period"
                            />
                            { formik.errors.expiryDuration && <div className="invalid-feedback">{formik.errors.expiryDuration}</div>}
                        </div>

                        {/* Submit Button */}
                        <div className="mt-4">
                            <Button type="submit" label={isEditing ? 'Update Fee' : 'Add Fee'} className="submit-button" loading={loading} />
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
}

export default AddEditFee;
